import React from 'react';

interface StrategicRecommendationsEngineProps {
  data: {
    ai_insights: Array<{
      category: string;
      insight: string;
      confidence: number;
      impact_score: number;
      urgency: 'low' | 'medium' | 'high' | 'critical';
      supporting_data: string[];
    }>;
    strategic_recommendations: Array<{
      recommendation: string;
      category: 'efficiency' | 'quality' | 'innovation' | 'collaboration' | 'risk_mitigation';
      priority: 'low' | 'medium' | 'high' | 'critical';
      expected_impact: number;
      implementation_effort: 'low' | 'medium' | 'high';
      timeline: string;
      success_probability: number;
      dependencies: string[];
      metrics_to_track: string[];
    }>;
    action_plans: Array<{
      plan_name: string;
      description: string;
      steps: Array<{
        step: string;
        duration: string;
        responsible: string;
        success_criteria: string;
      }>;
      expected_outcomes: string[];
      risk_factors: string[];
      mitigation_strategies: string[];
    }>;
    performance_gaps: Array<{
      gap_area: string;
      current_performance: number;
      target_performance: number;
      gap_size: number;
      root_causes: string[];
      improvement_opportunities: string[];
    }>;
    resource_optimization: {
      underutilized_resources: Array<{
        resource: string;
        utilization_rate: number;
        optimization_potential: number;
        recommendations: string[];
      }>;
      overutilized_resources: Array<{
        resource: string;
        utilization_rate: number;
        risk_level: 'low' | 'medium' | 'high';
        mitigation_strategies: string[];
      }>;
      resource_reallocation_opportunities: Array<{
        from_resource: string;
        to_resource: string;
        potential_benefit: number;
        implementation_complexity: 'low' | 'medium' | 'high';
      }>;
    };
  };
  loading?: boolean;
}

const StrategicRecommendationsEngine: React.FC<StrategicRecommendationsEngineProps> = ({ data, loading = false }) => {
  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Strategic Recommendations Engine</h3>
        <div className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Strategic Recommendations Engine</h3>
        <div className="h-64 flex items-center justify-center text-gray-500">
          No strategic recommendations available
        </div>
      </div>
    );
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'medium': return '#3b82f6';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'critical': return '#ef4444';
      case 'high': return '#f59e0b';
      case 'medium': return '#3b82f6';
      case 'low': return '#10b981';
      default: return '#6b7280';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'efficiency': return '⚡';
      case 'quality': return '🎯';
      case 'innovation': return '💡';
      case 'collaboration': return '🤝';
      case 'risk_mitigation': return '🛡️';
      default: return '📊';
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Strategic Recommendations Engine</h3>
      
      {/* AI Insights */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">AI-Powered Insights</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {data.ai_insights.map((insight, index) => {
            const urgency = getUrgencyColor(insight.urgency);
            return (
              <div key={index} className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg border-l-4" style={{ borderLeftColor: urgency }}>
                <div className="flex justify-between items-start mb-2">
                  <span className="font-medium text-gray-900">{insight.category}</span>
                  <span className="text-xs px-2 py-1 rounded" style={{ 
                    backgroundColor: urgency + '20', 
                    color: urgency 
                  }}>
                    {insight.urgency.toUpperCase()}
                  </span>
                </div>
                <p className="text-sm text-gray-700 mb-3">{insight.insight}</p>
                <div className="flex justify-between text-xs text-gray-600">
                  <span>Confidence: {(insight.confidence || 0).toFixed(1)}%</span>
                  <span>Impact: {(insight.impact_score || 0).toFixed(1)}%</span>
                </div>
                <div className="mt-2">
                  <div className="text-xs text-gray-500 mb-1">Supporting Data:</div>
                  <div className="space-y-1">
                    {insight.supporting_data.map((data, dataIndex) => (
                      <div key={dataIndex} className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        • {data}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Strategic Recommendations */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Strategic Recommendations</h4>
        <div className="space-y-4">
          {data.strategic_recommendations.map((rec, index) => {
            const priority = getPriorityColor(rec.priority);
            return (
              <div key={index} className="p-4 bg-white border border-gray-200 rounded-lg shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center">
                    <span className="text-lg mr-2">{getCategoryIcon(rec.category)}</span>
                    <span className="font-medium text-gray-900 capitalize">{rec.category}</span>
                  </div>
                  <span className="text-xs px-2 py-1 rounded" style={{ 
                    backgroundColor: priority + '20', 
                    color: priority 
                  }}>
                    {rec.priority.toUpperCase()} PRIORITY
                  </span>
                </div>
                <p className="text-sm text-gray-700 mb-3">{rec.recommendation}</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
                  <div>
                    <div className="text-gray-500">Expected Impact</div>
                    <div className="font-medium text-gray-900">{(rec.expected_impact || 0).toFixed(1)}%</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Implementation Effort</div>
                    <div className="font-medium text-gray-900 capitalize">{rec.implementation_effort}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Timeline</div>
                    <div className="font-medium text-gray-900">{rec.timeline}</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Success Probability</div>
                    <div className="font-medium text-gray-900">{(rec.success_probability || 0).toFixed(1)}%</div>
                  </div>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <div className="text-xs text-gray-500 mb-1">Dependencies:</div>
                  <div className="text-xs text-gray-600">{(rec.dependencies || []).join(', ')}</div>
                </div>
                <div className="mt-2">
                  <div className="text-xs text-gray-500 mb-1">Metrics to Track:</div>
                  <div className="flex flex-wrap gap-1">
                    {rec.metrics_to_track.map((metric, metricIndex) => (
                      <span key={metricIndex} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                        {metric}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Action Plans */}
      <div className="mb-6">
        <h4 className="text-md font-semibold text-gray-900 mb-3">Implementation Action Plans</h4>
        <div className="space-y-4">
          {data.action_plans.map((plan, index) => (
            <div key={index} className="p-4 bg-green-50 rounded-lg">
              <h5 className="font-medium text-green-900 mb-2">{plan.plan_name}</h5>
              <p className="text-sm text-green-800 mb-3">{plan.description}</p>
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <div>
                  <h6 className="font-medium text-green-900 mb-2">Implementation Steps</h6>
                  <div className="space-y-2">
                    {plan.steps.map((step, stepIndex) => (
                      <div key={stepIndex} className="p-2 bg-white rounded border-l-2 border-green-400">
                        <div className="text-sm font-medium text-gray-900">{step.step}</div>
                        <div className="text-xs text-gray-600 mt-1">
                          Duration: {step.duration} | Responsible: {step.responsible}
                        </div>
                        <div className="text-xs text-green-600 mt-1">
                          Success: {step.success_criteria}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <h6 className="font-medium text-green-900 mb-2">Expected Outcomes</h6>
                  <div className="space-y-1 mb-3">
                    {plan.expected_outcomes.map((outcome, outcomeIndex) => (
                      <div key={outcomeIndex} className="text-sm text-green-800">
                        • {outcome}
                      </div>
                    ))}
                  </div>
                  
                  <h6 className="font-medium text-green-900 mb-2">Risk Factors</h6>
                  <div className="space-y-1 mb-3">
                    {plan.risk_factors.map((risk, riskIndex) => (
                      <div key={riskIndex} className="text-sm text-red-600">
                        ⚠️ {risk}
                      </div>
                    ))}
                  </div>
                  
                  <h6 className="font-medium text-green-900 mb-2">Mitigation Strategies</h6>
                  <div className="space-y-1">
                    {plan.mitigation_strategies.map((strategy, strategyIndex) => (
                      <div key={strategyIndex} className="text-sm text-blue-600">
                        🛡️ {strategy}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Gaps & Resource Optimization */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div>
          <h4 className="font-medium text-gray-900 mb-3">Performance Gaps</h4>
          <div className="space-y-3">
            {data.performance_gaps.map((gap, index) => (
              <div key={index} className="p-3 bg-yellow-50 rounded-lg">
                <div className="flex justify-between items-start mb-2">
                  <span className="font-medium text-yellow-900">{gap.gap_area}</span>
                  <span className="text-sm font-bold text-yellow-600">
                    {(gap.gap_size || 0).toFixed(1)}% gap
                  </span>
                </div>
                <div className="text-sm text-yellow-800 mb-2">
                  Current: {(gap.current_performance || 0).toFixed(1)}% → Target: {(gap.target_performance || 0).toFixed(1)}%
                </div>
                <div className="text-xs text-yellow-700">
                  <strong>Root Causes:</strong> {(gap.root_causes || []).join(', ')}
                </div>
                <div className="text-xs text-yellow-700 mt-1">
                  <strong>Opportunities:</strong> {(gap.improvement_opportunities || []).join(', ')}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div>
          <h4 className="font-medium text-gray-900 mb-3">Resource Optimization</h4>
          <div className="space-y-3">
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">Underutilized Resources</h5>
              <div className="space-y-2">
                {data.resource_optimization.underutilized_resources.map((resource, index) => (
                  <div key={index} className="p-2 bg-blue-50 rounded">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-blue-900">{resource.resource}</span>
                      <span className="text-xs text-blue-600">
                        {(resource.utilization_rate || 0).toFixed(1)}% utilization
                      </span>
                    </div>
                    <div className="text-xs text-blue-700 mt-1">
                      Optimization potential: {(resource.optimization_potential || 0).toFixed(1)}%
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div>
              <h5 className="text-sm font-medium text-gray-700 mb-2">Resource Reallocation</h5>
              <div className="space-y-2">
                {data.resource_optimization.resource_reallocation_opportunities.map((opp, index) => (
                  <div key={index} className="p-2 bg-green-50 rounded">
                    <div className="text-sm font-medium text-green-900">
                      {opp.from_resource} → {opp.to_resource}
                    </div>
                    <div className="text-xs text-green-600">
                      Benefit: {(opp.potential_benefit || 0).toFixed(1)}% | Complexity: {opp.implementation_complexity}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StrategicRecommendationsEngine;